import { useState, Fragment } from "react";
import { Transition } from "@headlessui/react";
import {
  ArrowLeftStartOnRectangleIcon,
  ArrowsRightLeftIcon,
} from "@heroicons/react/20/solid";
import { useLocation, useMatch, useNavigate } from "react-router";
import * as React from "react";
import { Icon } from "@iconify/react";
import LogoutModal from "./LogoutModal";
import useIsMobile from "../../../utils/detectDevice";
import { HStack, TertiaryCTAButton, VStack } from "../../../components/utils";
import Avatar from "./Avatar";
import { useUserDetailsV2 } from "../../../queries";
import { getEquionSession } from "../../../api/idServer";
import {
  useCheckHeartBeat,
  useEmployeePortalAccess,
} from "../../../queries/idServer";
import "./FlyoutProfile.css";

const nudgeStoragePrefix = "employee-switch-nudge:";

export function FlyoutProfile({ profilePicUrl }: { profilePicUrl: string }) {
  const [dialogLogout, setDialogLogout] = useState(false);
  const [show, setShown] = useState(false);
  const [dialogChangePassword, setDialogChangePassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { isMobile } = useIsMobile(1025);

  const navigate = useNavigate();

  const contextRef = React.useRef<HTMLDivElement>(null);
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);
  const menuId = React.useId();
  const { data: userProfileDetails } = useUserDetailsV2();
  const [loading, setLoading] = useState(false);
  const [highlightEmployeeSwitch, setHighlightEmployeeSwitch] = useState(false);
  const userInteractedWithMenu = React.useRef(false);
  const highlightedSession = React.useRef<string | null>(null);
  const employeePortalUrl = process.env.REACT_APP_EMP_PORTAL;
  const { data: adminSession } = useCheckHeartBeat(false);

  const fullName =
    (userProfileDetails as any)?.full_name ??
    userProfileDetails?.fullName ??
    "";
  const emailId =
    (userProfileDetails as any)?.email_id ??
    userProfileDetails?.emailId ??
    (userProfileDetails as any)?.email ??
    "";
  const {
    data: hasEmployeePortalAccess,
    isError: employeeAccessLookupFailed,
    isFetching: checkingEmployeePortalAccess,
    refetch: retryEmployeeAccessLookup,
  } = useEmployeePortalAccess(getEquionSession());

  React.useEffect(() => {
    if (
      !adminSession?.showEmployeeSwitchNudge ||
      !adminSession.sid ||
      hasEmployeePortalAccess !== true ||
      !employeePortalUrl
    ) {
      return undefined;
    }
    if (highlightedSession.current === adminSession.sid) return undefined;

    const storageKey = `${nudgeStoragePrefix}${adminSession.userId}`;
    const startHighlight = () => {
      if (document.visibilityState === "hidden") return;
      if (highlightedSession.current === adminSession.sid) return;
      try {
        if (window.localStorage.getItem(storageKey) === adminSession.sid)
          return;
        window.localStorage.setItem(storageKey, adminSession.sid);
      } catch {
        // Storage can be unavailable in a private browser context.
      }

      highlightedSession.current = adminSession.sid;
      setShown(true);
      setHighlightEmployeeSwitch(true);
      document.removeEventListener("visibilitychange", startHighlight);
    };
    document.addEventListener("visibilitychange", startHighlight);
    startHighlight();
    return () =>
      document.removeEventListener("visibilitychange", startHighlight);
  }, [adminSession, hasEmployeePortalAccess, employeePortalUrl]);

  React.useEffect(() => {
    if (
      !highlightEmployeeSwitch ||
      !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setHighlightEmployeeSwitch(false);
      if (!userInteractedWithMenu.current) setShown(false);
    }, 12000);
    return () => window.clearTimeout(timer);
  }, [highlightEmployeeSwitch]);

  React.useLayoutEffect(() => {
    if (!highlightEmployeeSwitch || !show) return undefined;
    document.body.classList.add("employee-switch-backdrop-active");
    return () =>
      document.body.classList.remove("employee-switch-backdrop-active");
  }, [highlightEmployeeSwitch, show]);

  React.useEffect(() => {
    if (!show) return undefined;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!contextRef.current?.contains(event.target as Node)) {
        setShown(false);
        setHighlightEmployeeSwitch(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setShown(false);
      setHighlightEmployeeSwitch(false);
      menuButtonRef.current?.focus();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [show]);

  const finishEmployeeSwitchHighlight = () => {
    setHighlightEmployeeSwitch(false);
    if (!userInteractedWithMenu.current) setShown(false);
  };

  const routes = [
    {
      name: "Log Out",
      onClick: () => {
        setDialogLogout(true);
        setShown(false);
      },
      type: "heroIcon",
      icon: ArrowLeftStartOnRectangleIcon,
    },
  ];

  const profileMatch = useMatch(`/${"/user/profile".split("/")[1]}/*`);
  const { pathname } = useLocation();
  const showHighlightBackdrop = highlightEmployeeSwitch && show;

  return (
    <div className={showHighlightBackdrop ? "relative z-50" : undefined}>
      {showHighlightBackdrop && (
        <div
          aria-hidden="true"
          data-employee-switch-backdrop="true"
          className="pointer-events-none fixed inset-0 z-0 bg-white/10 backdrop-blur-sm"
        />
      )}
      {loading ? (
        <div className="items-center px-6 py-2">
          <p className="text-sm animate-pulse font-medium">Switching...</p>
        </div>
      ) : (
        <div
          className={showHighlightBackdrop ? "relative z-10" : "relative"}
          ref={contextRef}
        >
          {dialogLogout && (
            <LogoutModal
              isOpen={dialogLogout}
              onClose={() => {
                setDialogLogout(false);
              }}
            />
          )}
          {/* TODO: need to navigate to Change Password Page */}
          <div className="relative">
            {highlightEmployeeSwitch && (
              <span role="status" className="sr-only">
                Switch to employee portal is available in the user menu.
              </span>
            )}
            <div>
              <button
                ref={menuButtonRef}
                type="button"
                aria-controls={menuId}
                aria-expanded={show}
                onClick={() => {
                  userInteractedWithMenu.current = true;
                  setShown(!show);
                  if (show) setHighlightEmployeeSwitch(false);
                }}
                aria-label="User menu"
                onFocus={() => {
                  userInteractedWithMenu.current = true;
                }}
                onMouseEnter={() => {
                  userInteractedWithMenu.current = true;
                  setShown(true);
                }}
                className={`-ml-1.5 flex gap-2 cursor-pointer items-center p-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
                  pathname !== "/profile"
                    ? "bg-white text-secondary"
                    : "bg-secondary text-white"
                } `}
              >
                <div className="flex items-center justify-center flex-none mt-1 rounded-lg h-11 w-11 bg-gray-50 group-hover:bg-white">
                  <Avatar name={fullName || ""} profilePicUrl={profilePicUrl} />
                </div>
              </button>
            </div>
            <Transition
              show={show}
              as={Fragment}
              enter="motion-safe:transition motion-safe:ease-out motion-safe:duration-200"
              enterFrom="motion-safe:opacity-0 motion-safe:translate-y-1"
              enterTo="motion-safe:opacity-100 motion-safe:translate-y-0"
              leave="motion-safe:transition motion-safe:ease-in motion-safe:duration-150"
              leaveFrom="motion-safe:opacity-100 motion-safe:translate-y-0"
              leaveTo="motion-safe:opacity-0 motion-safe:translate-y-1"
            >
              <div
                id={menuId}
                onMouseEnter={() => {
                  userInteractedWithMenu.current = true;
                  setShown(true);
                }}
                onFocus={() => {
                  userInteractedWithMenu.current = true;
                }}
                onMouseLeave={() => {
                  setShown(false);
                  setHighlightEmployeeSwitch(false);
                }}
                className="absolute z-10 mt-5 flex w-screen max-w-max  right-0 "
              >
                <div className="w-screen max-w-xs flex-auto overflow-hidden rounded-lg bg-white text-sm leading-6 shadow-lg">
                  <div className="divide-x divide-gray-900/5 bg-gray-50">
                    <div className="group relative flex gap-x-2 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-center flex-none mt-1 rounded-lg h-11 w-11 bg-gray-50 group-hover:bg-white">
                        <Avatar
                          name={fullName || ""}
                          profilePicUrl={profilePicUrl}
                        />
                      </div>
                      <VStack className="w-full">
                        <HStack className="justify-between items-center w-full">
                          <a className="font-semibold text-gray-900 flex-1">
                            {fullName}
                            <span className="absolute inset-0" />
                          </a>
                        </HStack>

                        <p className="text-gray-600 whitespace-nowrap">
                          {emailId}
                        </p>
                        <p className="text-sm text-gray-500 text-ellipsis overflow-clip"></p>
                      </VStack>
                    </div>
                  </div>

                  <div className="p-2">
                    {!employeeAccessLookupFailed &&
                      hasEmployeePortalAccess === true &&
                      employeePortalUrl && (
                        <a
                          href={employeePortalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Switch to employee portal (opens in a new tab)"
                          onAnimationEnd={(event) => {
                            if (event.target === event.currentTarget)
                              finishEmployeeSwitchHighlight();
                          }}
                          className={`group relative flex w-full items-center gap-4 rounded-lg p-2 text-headerColor hover:bg-slate-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
                            highlightEmployeeSwitch
                              ? "employee-switch-nudge"
                              : ""
                          }`}
                        >
                          <span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-slate-light group-hover:bg-white">
                            <ArrowsRightLeftIcon
                              className="h-6 w-6 text-primaryText group-hover:text-primary"
                              aria-hidden="true"
                            />
                          </span>
                          <span className="font-semibold group-hover:text-primary">
                            Switch to employee portal
                          </span>
                        </a>
                      )}
                    {employeeAccessLookupFailed && (
                      <VStack className="gap-2 p-2">
                        <p className="text-xs2 text-secondaryText">
                          Could not check your employee portal access.
                        </p>
                        <TertiaryCTAButton
                          onClick={() => retryEmployeeAccessLookup()}
                          disabled={checkingEmployeePortalAccess}
                          className="w-full justify-center focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-wait disabled:opacity-60"
                        >
                          {checkingEmployeePortalAccess
                            ? "Checking..."
                            : "Try again"}
                        </TertiaryCTAButton>
                      </VStack>
                    )}
                    {routes.map((item) => (
                      <button
                        key={item.name}
                        type="button"
                        onClick={() => {
                          item.onClick();
                        }}
                        className="group relative flex w-full items-center gap-4 rounded-lg p-2 text-left text-headerColor hover:bg-slate-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                      >
                        <span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-slate-light group-hover:bg-white">
                          {item?.type !== "iconify" && (
                            <item.icon
                              className={`h-6 w-6 text-primaryText group-hover:text-primary ${
                                profileMatch && item.name === "Profile"
                                  ? "text-primary"
                                  : ""
                              }`}
                              aria-hidden="true"
                            />
                          )}
                          {item?.type === "iconify" &&
                            typeof item.icon === "string" && (
                              <Icon
                                icon={item.icon || ""}
                                className={`h-6 w-6 text-primaryText group-hover:text-primary ${
                                  profileMatch && item.name === "Profile"
                                    ? "fill-primary"
                                    : ""
                                }`}
                              />
                            )}
                        </span>
                        <span
                          className={`font-semibold group-hover:text-primary ${
                            profileMatch && item.name === "Profile"
                              ? "text-primary"
                              : ""
                          }`}
                        >
                          {item.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </div>
      )}
    </div>
  );
}
